import React, { useEffect, useState } from 'react';
import Modal from '../UI/Modal';
import Button from '../UI/Button';
import spinService, {
  SpinPrizeItem,
  SpinPrizeType,
} from '../../api/services/spin.service';

interface SpinPrizeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  prize?: SpinPrizeItem | null;
}

const inputClass =
  'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400';
const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

const SpinPrizeFormModal: React.FC<SpinPrizeFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  prize,
}) => {
  const isEdit = !!prize;

  const [label, setLabel] = useState('');
  const [labelAlt, setLabelAlt] = useState('');
  const [type, setType] = useState<SpinPrizeType>('money');
  const [amount, setAmount] = useState(0);
  const [probability, setProbability] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [dailyLimit, setDailyLimit] = useState<number | null>(null);
  const [sortOrder, setSortOrder] = useState(0);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    if (prize) {
      setLabel(prize.label);
      setLabelAlt(prize.labelAlt || '');
      setType(prize.type);
      setAmount(prize.amount || 0);
      setProbability(prize.probability || 0);
      setIsActive(prize.isActive);
      setDailyLimit(prize.dailyLimit ?? null);
      setSortOrder(prize.sortOrder || 0);
    } else {
      setLabel('');
      setLabelAlt('');
      setType('money');
      setAmount(0);
      setProbability(0);
      setIsActive(true);
      setDailyLimit(null);
      setSortOrder(0);
    }
    setError(null);
  }, [isOpen, prize]);

  const handleSubmit = async () => {
    setError(null);

    if (!label.trim()) return setError('Label is required');
    if (probability < 0) return setError('Probability cannot be negative');
    if (type === 'money' && amount <= 0) {
      return setError('Money prize must have an amount greater than 0');
    }

    setSaving(true);
    try {
      const payload = {
        label: label.trim(),
        labelAlt: labelAlt.trim(),
        type,
        amount: type === 'money' ? amount : 0,
        probability,
        isActive,
        dailyLimit:
          dailyLimit === null || dailyLimit === undefined
            ? null
            : Number(dailyLimit),
        sortOrder,
      };

      if (isEdit && prize) {
        await spinService.updatePrize(prize._id, payload);
      } else {
        await spinService.createPrize(payload);
      }

      onSuccess();
      onClose();
    } catch (e: any) {
      setError(
        e?.response?.data?.message || e?.message || 'Failed to save prize'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Prize' : 'Add Prize'}
      size="md"
    >
      <div className="space-y-4">
        {error && (
          <div className="text-xs bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className={labelClass}>Label (Amharic)</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="10 ብር"
              className={inputClass}
            />
          </div>

          <div className="md:col-span-2">
            <label className={labelClass}>Label Alt (English, optional)</label>
            <input
              type="text"
              value={labelAlt}
              onChange={(e) => setLabelAlt(e.target.value)}
              placeholder="10 ETB"
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as SpinPrizeType)}
              className={inputClass}
            >
              <option value="money">Money</option>
              <option value="item">Item</option>
              <option value="nothing">Nothing</option>
              <option value="spin_again">Spin Again</option>
            </select>
          </div>

          <div>
            <label className={labelClass}>Amount (ETB)</label>
            <input
              type="number"
              min={0}
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value) || 0)}
              disabled={type !== 'money'}
              className={`${inputClass} ${
                type !== 'money' ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
            />
            <p className="text-[10px] text-gray-500 mt-1">
              Only used when type is "Money".
            </p>
          </div>

          <div>
            <label className={labelClass}>Probability (weight)</label>
            <input
              type="number"
              min={0}
              value={probability}
              onChange={(e) => setProbability(Number(e.target.value) || 0)}
              className={inputClass}
            />
            <p className="text-[10px] text-gray-500 mt-1">
              Higher = more likely. 0 = never picked.
            </p>
          </div>

          <div>
            <label className={labelClass}>Sort Order</label>
            <input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
              className={inputClass}
            />
            <p className="text-[10px] text-gray-500 mt-1">
              Lower numbers appear first on the wheel.
            </p>
          </div>

          <div>
            <label className={labelClass}>Daily Limit (optional)</label>
            <input
              type="number"
              min={0}
              value={dailyLimit ?? ''}
              onChange={(e) =>
                setDailyLimit(
                  e.target.value === '' ? null : Number(e.target.value) || 0
                )
              }
              placeholder="Leave empty for unlimited"
              className={inputClass}
            />
          </div>

          <div className="flex items-center gap-2 pt-6">
            <input
              id="spin-prize-active"
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="w-4 h-4 accent-primary-500"
            />
            <label
              htmlFor="spin-prize-active"
              className="text-sm text-gray-700 cursor-pointer"
            >
              Active
            </label>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Prize'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SpinPrizeFormModal;